import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Plus, User, Plug, Mail, ExternalLink, Send, Pencil, Save, RotateCcw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useAuth } from '@core/auth/useAuth';
import { supabase } from '@core/supabase/client';
import { jobService } from '@core/supabase/services/jobService';
import { orgService } from '@core/supabase/services/orgService';
import { applicationService } from '@core/supabase/services/applicationService';
import { scheduledJobService } from '@core/supabase/services/scheduledJobService';
import { Job, Application, ApplicationStatus, JobPublishStatus, JOB_PUBLISH_STATUS_LABELS } from '@core/types';
import { metaForChannel, normalizeJobPublishChannels } from '@core/constants/jobPublishChannels';
import { JobMatchesTab } from './tabs/JobMatchesTab';
import { JobLifecycleModal } from './JobLifecycleModal';
import { JobForm } from './JobForm';
import { JobApplicationsPanel } from './JobApplicationsPanel';
import { ActivityHeartbeatDot, isRecentActivity } from '@modules/candidates/candidateActivityUi';
import { PIPELINE_STATUS_LABEL } from '@core/constants/pipelineStages';
import { RecordHeader } from '@shared/components/ui/RecordHeader';
import { RecordDetailLayout } from '@shared/components/ui/RecordDetailLayout';
import { RecordMetaCard } from '@shared/components/ui/RecordMetaCard';
import { RightRailWithAssistant } from '@shared/components/ui/RightRailWithAssistant';
import { useTrackRecentlyViewed } from '@shared/hooks/useRecentlyViewed';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { detailTbBtn, detailTbNeutral, detailTbEdit, detailTbDanger } from '@shared/lib/detailToolbarStyles';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { StatusBadge } from '@shared/components/ui/StatusBadge';
import { CandidateCollapsiblePanel } from '@modules/candidates/CandidateCollapsiblePanel';
import { rechartsGridStroke, rechartsTooltipStyle } from '@shared/chart/rechartsTheme';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { readJdTaxonomyFromJob } from './jdStructuredTaxonomy';

const VALID_TABS = ['overview', 'applications', 'matches', 'integration', 'parameters'] as const;
type TabId = (typeof VALID_TABS)[number];

const JOB_STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  open:    { bg: 'rgba(16,185,129,0.12)', text: '#10b981', border: 'rgba(16,185,129,0.3)' },
  on_hold: { bg: 'rgba(245,158,11,0.12)', text: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
  filled:  { bg: 'rgba(99,102,241,0.12)', text: '#6366f1', border: 'rgba(99,102,241,0.3)' },
  closed:  { bg: 'rgba(107,114,128,0.12)', text: '#6b7280', border: 'rgba(107,114,128,0.3)' },
};

const PUBLISH_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft:          { bg: 'rgba(139,128,249,0.12)', text: '#8B80F9' },
  pending_review: { bg: 'rgba(245,158,11,0.12)',  text: '#f59e0b' },
  published:      { bg: 'rgba(16,185,129,0.12)',  text: '#10b981' },
  scheduled:      { bg: 'rgba(14,165,233,0.12)',  text: '#0ea5e9' },
  paused:         { bg: 'rgba(245,158,11,0.12)',  text: '#f59e0b' },
  closed:         { bg: 'rgba(239,68,68,0.12)',   text: '#ef4444' },
};

function fmtDate(iso: string | null | undefined) {
  return formatDateTimeGmt(iso);
}
function fmtDateTime(iso: string | null | undefined) {
  return formatDateTimeGmt(iso);
}

function recordInitials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [job, setJob] = useState<Job | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [formKey, setFormKey] = useState(0);
  const [updatingPublish, setUpdatingPublish] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<string>('');
  const [orgSlug, setOrgSlug] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [lifecycleModalOpen, setLifecycleModalOpen] = useState(false);
  const [isIntegrationEditing, setIsIntegrationEditing] = useState(false);
  const [lifecycleInitialAxis, setLifecycleInitialAxis] = useState<'ops' | 'publish'>('ops');
  const { user } = useAuth();
  const navigate = useNavigate();

  useTrackRecentlyViewed(
    useMemo(
      () =>
        job
          ? {
              kind: 'job' as const,
              id: job.id,
              label: job.title || 'Job',
              sublabel:
                ((job as { client?: { name?: string } }).client?.name ||
                  (job.status ? `Status: ${job.status}` : undefined)) ?? undefined,
              to: `/jobs/${job.id}`,
            }
          : null,
      [job?.id, job?.title, job?.status, (job as { client?: { name?: string } } | null)?.client?.name],
    ),
  );

  const tabFromUrl = searchParams.get('tab') as TabId | null;
  const [activeTab, setActiveTab] = useState<TabId>(
    VALID_TABS.includes(tabFromUrl ?? ('' as TabId)) ? tabFromUrl! : 'overview'
  );

  useEffect(() => {
    const tab = searchParams.get('tab') as TabId | null;
    if (tab && VALID_TABS.includes(tab)) setActiveTab(tab);
  }, [searchParams]);

  const setTab = (tab: TabId) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'overview') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
        next.delete('edit');
      }
      return next;
    }, { replace: true });
  };

  const editQuery = searchParams.get('edit');
  useEffect(() => {
    if (activeTab !== 'overview' && editQuery === '1') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      }, { replace: true });
    }
  }, [activeTab, editQuery, setSearchParams]);

  useEffect(() => {
    if (!id) return;
    const storageKey = 'ggj.b2b.jobDetail.id';
    const lastId = sessionStorage.getItem(storageKey);
    if (lastId !== id) {
      sessionStorage.setItem(storageKey, id);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      }, { replace: true });
    }
    jobService.getById(id).then(setJob).finally(() => setLoading(false));
  }, [id, setSearchParams]);

  useEffect(() => {
    if (!user?.orgId || !id) return;
    applicationService.list(user.orgId, { jobId: id }).then((list) =>
      setApplications(Array.isArray(list) ? list : [])
    );
  }, [user?.orgId, id]);

  useEffect(() => {
    if (!user?.orgId) return;
    void orgService.getOrg(user.orgId).then((o) => {
      if (o?.slug) setOrgSlug(o.slug);
      if (o?.name) setOrgName(o.name);
    });
  }, [user?.orgId]);

  useEffect(() => {
    const l = searchParams.get('lifecycle');
    if (l !== 'ops' && l !== 'publish') return;
    setLifecycleInitialAxis(l as 'ops' | 'publish');
    setLifecycleModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('lifecycle');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const uniqueCandidates = useMemo(() => {
    const seen = new Set<string>();
    return applications.filter((a) => {
      if (!a.candidate_id || seen.has(a.candidate_id)) return false;
      seen.add(a.candidate_id);
      return true;
    });
  }, [applications]);

  const applicationsTabHeartbeat = useMemo(
    () =>
      applications.some(
        (a) => isRecentActivity(a.updated_at) || isRecentActivity(a.status_updated_at)
      ) && applications.length > 0,
    [applications]
  );

  const sortedAppsForJobRail = useMemo(
    () =>
      [...applications].sort(
        (a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()
      ),
    [applications]
  );

  const jdTaxonomy = useMemo(
    () => readJdTaxonomyFromJob(job ?? { jd_structured_json: null }),
    [job]
  );

  /** Client Parameters tab – style dt/dd */
  const PARAM_DT = 'vscode-text-secondary pr-2 min-w-0';
  const PARAM_DD = 'vscode-text-primary min-w-0 break-words';

  const heartbeatData = useMemo(() => {
    const now = new Date();
    const weeks: { week: string; count: number }[] = [];
    for (let i = 7; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i * 7);
      const weekStart = new Date(d);
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      const count = applications.filter((a) => {
        const t = new Date(a.submitted_at).getTime();
        return t >= weekStart.getTime() && t < weekEnd.getTime();
      }).length;
      weeks.push({ week: formatDateTimeGmt(d.toISOString()), count });
    }
    return weeks;
  }, [applications]);


  if (loading) return <FullPageSpinner />;
  if (!job) return <div className="vscode-text-secondary">Job not found.</div>;
  if (!user?.orgId) return null;

  const isEditing = activeTab === 'overview' && searchParams.get('edit') === '1';

  const clientHref = job.client_id ? `/clients/${job.client_id}` : '/clients';
  const statusColors = JOB_STATUS_COLORS[job.status] ?? JOB_STATUS_COLORS.closed;
  const publishColors = PUBLISH_STATUS_COLORS[job.publish_status ?? 'draft'] ?? PUBLISH_STATUS_COLORS.draft;

  const tabs: { id: TabId; label: string }[] = [
    { id: 'overview',     label: 'Overview' },
    { id: 'applications', label: applications.length > 0 ? `Applications (${applications.length})` : 'Applications' },
    { id: 'matches',      label: 'Smart matches' },
    { id: 'integration',  label: 'Client sync' },
    { id: 'parameters',   label: 'Parameters' },
  ];

  return (
    <div className="w-full min-w-0 mx-auto max-w-none">
      <JobLifecycleModal
        open={lifecycleModalOpen}
        onClose={() => setLifecycleModalOpen(false)}
        job={job}
        orgId={user.orgId}
        orgSlug={orgSlug}
        orgName={orgName}
        initialAxis={lifecycleInitialAxis}
        onJobUpdated={(j) => setJob(j)}
      />

      <RecordDetailLayout
        header={
          <RecordHeader
            avatarInitials={recordInitials(job.title)}
            title={job.title}
            meta={`${job.client?.name ?? '—'} · ${JOB_PUBLISH_STATUS_LABELS[job.publish_status ?? 'draft'] ?? job.publish_status ?? '—'} · ${job.status}`}
            breadcrumb={[
              { label: 'Data', href: '/jobs' },
              { label: 'Jobs', href: '/jobs' },
              { label: job.title },
            ]}
            actionsEnd={
              <ListOperationsDropdown
                objectHistoryHref={objectHistoryUrl('jobs')}
                onExport={(fmt, scope) => listExportNotImplemented('Job', fmt, scope)}
              />
            }
          />
        }
        metaRail={
          <RightRailWithAssistant
            recordId={job.id}
            entityKind="job"
            details={
              <RecordMetaCard
                title="Record"
                rows={[
                  { label: 'Created', value: fmtDateTime(job.created_at) },
                  { label: 'Updated', value: '—' },
                  { label: 'Client', value: job.client?.name ?? '—' },
                  { label: 'Status', value: job.status },
                  { label: 'Openings', value: job.openings_count != null ? String(job.openings_count) : '—' },
                  { label: 'Applications', value: String(applications.length) },
                ]}
                footer={
                  <Link to={objectHistoryUrl('jobs')} className="text-sm text-[var(--vscode-text-link)] hover:underline">
                    View history
                  </Link>
                }
              />
            }
          />
        }
        main={
          <>
      {/* Tab nav */}
      <nav className="ggj-cand-tablist mb-4" aria-label="Job sections">
          {tabs.map((tab) => (
            <button
              key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
              onClick={() => setTab(tab.id)}
            className={`ggj-cand-tab ${activeTab === tab.id ? 'ggj-cand-tab--active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

      {/* ─────────────────────────────────────────────────────────
          OVERVIEW TAB
      ───────────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(340px,30%)] gap-3 lg:gap-5 items-start">

          {/* Left: main job card */}
          <div className="ggj-param-group overflow-hidden order-1">
            {/* Toolbar */}
            <div className="ggj-profile-toolbar">
              <div className="flex flex-wrap items-center gap-2">
                {isEditing ? (
                  <>
                    <button type="submit" form="job-form" className={`${detailTbBtn} ${detailTbNeutral}`}>
                      <Save className="gg-toolbar-ic gg-toolbar-ic--violet shrink-0" aria-hidden />
                      <span>[Save]</span>
                    </button>
                    <button
                      type="button"
                      className={`${detailTbBtn} ${detailTbDanger}`}
                      onClick={() => {
                        setSearchParams((prev) => {
                          const next = new URLSearchParams(prev);
                          next.delete('edit');
                          return next;
                        }, { replace: true });
                        setFormKey((k) => k + 1);
                      }}
                    >
                      <RotateCcw className="gg-toolbar-ic gg-toolbar-ic--red shrink-0" aria-hidden />
                      <span>[Discard]</span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={`${detailTbBtn} ${detailTbEdit}`}
                    onClick={() => {
                      setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.set('edit', '1');
                        return next;
                      }, { replace: true });
                    }}
                  >
                    <Pencil className="h-4 w-4 shrink-0 text-[var(--vscode-text-link)]" aria-hidden />
                    <span>Edit</span>
                  </button>
                )}
              </div>
            </div>

            {/* ── Job context summary ── */}
            <div className="ggj-profile-section-h">
              {job.client?.name ? job.client.name.toUpperCase() : 'JOB OVERVIEW'}
                          </div>
            <div className="ggj-profile-section-body">
              <div className="flex flex-wrap items-center gap-4">
                {/* Job avatar */}
                <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold"
                  style={{ background: 'rgba(139,128,249,0.15)', color: '#8B80F9', border: '1px solid rgba(139,128,249,0.3)' }}>
                  {job.title.slice(0, 2).toUpperCase()}
              </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-bold vscode-text-primary truncate">{job.title}</p>
                  {job.client && (
                    <Link to={clientHref} className="text-sm vscode-text-link">
                      {job.client.name}
                    </Link>
                  )}
                  {job.location && <p className="text-xs vscode-text-tertiary mt-0.5">{job.location}</p>}
          </div>
              <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: statusColors.bg, color: statusColors.text, border: `1px solid ${statusColors.border}` }}>
                    {job.status.replace(/_/g, ' ')}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: publishColors.bg, color: publishColors.text }}>
                    {(job.publish_status ?? 'draft').replace(/_/g, ' ')}
                  </span>
              </div>
            </div>
              <div className="flex flex-wrap gap-5 mt-3 pt-3 border-t vscode-border">
                <div className="text-center">
                  <p className="text-lg font-bold vscode-text-primary">{applications.length}</p>
                  <p className="text-[11px] vscode-text-tertiary uppercase tracking-wide">Applications</p>
            </div>
                <div className="text-center">
                  <p className="text-lg font-bold vscode-text-primary">{uniqueCandidates.length}</p>
                  <p className="text-[11px] vscode-text-tertiary uppercase tracking-wide">Candidates</p>
              </div>
                <div className="text-center">
                  <p className="text-lg font-bold vscode-text-primary">
                    {applications.filter(a => a.status === 'placed').length}
                  </p>
                  <p className="text-[11px] vscode-text-tertiary uppercase tracking-wide">Placed</p>
              </div>
                <div className="text-center">
                  <p className="text-lg font-bold vscode-text-primary">
                    {applications.filter(a => ['interview_1_scheduled','interview_1_conducted','interview_2_scheduled','interview_2_conducted'].includes(a.status)).length}
                  </p>
                  <p className="text-[11px] vscode-text-tertiary uppercase tracking-wide">Interviewing</p>
                </div>
              </div>
            </div>

            {/* Job form — always rendered, readOnly when not editing (same pattern as CandidateDetailPage) */}
            <JobForm
              key={formKey}
              mode="edit"
              job={job}
              readOnly={!isEditing}
              orgId={user.orgId}
              userId={user.id}
              onSave={(updated) => {
                setJob(updated);
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('edit');
                  return next;
                }, { replace: true });
              }}
            />

            {/* ── Publishing & Distribution — read-only reference section ── */}
            <div className="ggj-profile-section-h">PUBLISHING &amp; DISTRIBUTION</div>
            <div className="ggj-profile-section-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                {/* Publish status — interactive even in view mode */}
                <div className="grid grid-cols-[minmax(130px,155px)_1fr] gap-x-3 items-start border-b border-[var(--vscode-border-divider)] py-[0.65rem]">
                  <span className="text-sm vscode-text-secondary leading-snug pt-1.5 pr-2">Publish status</span>
              <div>
                <select
                      className="input w-full max-w-[180px] text-sm"
                  value={job.publish_status ?? 'draft'}
                  disabled={updatingPublish}
                  onChange={async (e) => {
                    const newStatus = e.target.value as JobPublishStatus;
                    if (newStatus === 'scheduled') {
                      setJob((prev) => prev ? { ...prev, publish_status: 'scheduled' } : prev);
                      return;
                    }
                    setUpdatingPublish(true);
                    try {
                          if (user?.orgId) await scheduledJobService.cancelPendingPublishJobsForB2BJob(user.orgId, job.id);
                      const { error } = await supabase
                        .from('ggj_b2b_jobs')
                        .update({
                          publish_status: newStatus,
                          published_at: newStatus === 'published' ? new Date().toISOString() : job.published_at,
                          scheduled_publish_at: null,
                          scheduled_publish_job_id: null,
                        })
                        .eq('id', job.id);
                      if (!error) {
                            setJob((prev) => prev ? {
                                ...prev,
                                publish_status: newStatus,
                                scheduled_publish_at: null,
                                scheduled_publish_job_id: null,
                            } : prev);
                          }
                        } finally { setUpdatingPublish(false); }
                      }}>
                  {(Object.entries(JOB_PUBLISH_STATUS_LABELS) as [JobPublishStatus, string][]).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
                  </div>
                </div>
                <div className="grid grid-cols-[minmax(130px,155px)_1fr] gap-x-3 items-start border-b border-[var(--vscode-border-divider)] py-[0.65rem]">
                  <span className="text-sm vscode-text-secondary leading-snug pt-1.5 pr-2">Published at</span>
                  <span className="text-sm vscode-text-primary pt-1.5">{fmtDate(job.published_at)}</span>
                </div>
                <div className="grid grid-cols-[minmax(130px,155px)_1fr] gap-x-3 items-start border-b border-[var(--vscode-border-divider)] py-[0.65rem]">
                  <span className="text-sm vscode-text-secondary leading-snug pt-1.5 pr-2">Scheduled pub.</span>
                  <span className="text-sm vscode-text-primary pt-1.5">{fmtDateTime(job.scheduled_publish_at)}</span>
                </div>
                <div className="grid grid-cols-[minmax(130px,155px)_1fr] gap-x-3 items-start border-b border-[var(--vscode-border-divider)] py-[0.65rem]">
                  <span className="text-sm vscode-text-secondary leading-snug pt-1.5 pr-2">Auto-unpublish</span>
                  <span className="text-sm vscode-text-primary pt-1.5">{fmtDateTime(job.scheduled_unpublish_at)}</span>
                </div>
                <div className="grid grid-cols-[minmax(130px,155px)_1fr] gap-x-3 items-start border-b border-[var(--vscode-border-divider)] py-[0.65rem]">
                  <span className="text-sm vscode-text-secondary leading-snug pt-1.5 pr-2">Careers URL</span>
                  <span className="text-sm vscode-text-primary pt-1.5">
                    {job.publish_status === 'published' && job.status === 'open' && orgSlug ? (
                      <a href={`${window.location.origin}/careers/${encodeURIComponent(orgSlug)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="vscode-text-link inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> /careers/{orgSlug}
                      </a>
                    ) : '—'}
                  </span>
                </div>
                <div className="grid grid-cols-[minmax(130px,155px)_1fr] gap-x-3 items-start border-b border-[var(--vscode-border-divider)] py-[0.65rem] md:col-span-2">
                  <span className="text-sm vscode-text-secondary leading-snug pt-1.5 pr-2">Channels</span>
                  <div className="pt-1.5">
                    {normalizeJobPublishChannels(job.publish_channels).length === 0 ? (
                      <span className="text-sm vscode-text-tertiary">None — set via publish flow</span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {normalizeJobPublishChannels(job.publish_channels).map((cid) => {
                          const m = metaForChannel(cid);
                          return (
                            <span key={cid} className="text-xs px-2.5 py-1 rounded-full vscode-text-secondary"
                              style={{ background: 'var(--vscode-bg-tertiary)', border: '1px solid var(--vscode-border-primary)' }}>
                              {m?.label ?? cid}
                              {m && !m.live ? <span className="text-amber-500/80 ml-1">· planned</span> : null}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

                {job.publish_status === 'scheduled' && (
                <div className="mt-3 p-3 rounded-lg border vscode-border space-y-2"
                  style={{ background: 'color-mix(in srgb, var(--vscode-bg-tertiary) 60%, transparent)' }}>
                  <p className="text-xs font-semibold vscode-text-secondary">Schedule publish at</p>
                    <input
                      type="datetime-local"
                    className="input text-sm"
                      value={scheduledAt || (job.scheduled_publish_at ? job.scheduled_publish_at.slice(0, 16) : '')}
                      min={new Date().toISOString().slice(0, 16)}
                      onChange={(e) => setScheduledAt(e.target.value)}
                    />
                    <button
                    type="button"
                    className="btn-primary text-xs py-1.5 px-4"
                      disabled={!scheduledAt || updatingPublish}
                      onClick={async () => {
                      if (!scheduledAt || !user?.orgId) return;
                        setUpdatingPublish(true);
                        try {
                        await scheduledJobService.scheduleJobPublish(user.orgId, job.id, new Date(scheduledAt).toISOString());
                          const refreshed = await jobService.getById(job.id);
                          if (refreshed) setJob(refreshed);
                          setScheduledAt('');
                      } finally { setUpdatingPublish(false); }
                    }}>
                      {updatingPublish ? 'Saving…' : 'Save schedule'}
                    </button>
                  </div>
                )}
                  </div>

            {/* ── Dates & Timeline ── */}
            <div className="ggj-profile-section-h">DATES &amp; TIMELINE</div>
            <div className="ggj-profile-section-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                {[
                  { label: 'Opened',         value: fmtDate(job.opened_at) },
                  { label: 'Created',        value: fmtDate(job.created_at) },
                  { label: 'Filled',         value: fmtDate(job.filled_at) },
                  { label: 'Published',      value: fmtDate(job.published_at) },
                  { label: 'Sched. publish', value: fmtDateTime(job.scheduled_publish_at) },
                  { label: 'Sched. unpub.',  value: fmtDateTime(job.scheduled_unpublish_at) },
                ].map(({ label, value }) => (
                  <div key={label}
                    className="grid grid-cols-[minmax(130px,155px)_1fr] gap-x-3 items-start border-b border-[var(--vscode-border-divider)] py-[0.65rem] last:border-b-0">
                    <span className="text-sm vscode-text-secondary leading-snug pt-1.5 pr-2">{label}</span>
                    <span className="text-sm vscode-text-primary pt-1.5">{value}</span>
                    </div>
                ))}
                </div>
              </div>
              </div>

          {/* Right: sticky sidebar */}
          <aside className="ggj-rail-aside space-y-2 order-2 lg:sticky lg:top-4 self-start">

            {/* Hiring heartbeat */}
            <CandidateCollapsiblePanel storageKey={`${job.id}-heartbeat`} title="Hiring heartbeat">
              {heartbeatData.every((d) => d.count === 0) ? (
                <div className="py-4 text-center">
                  <p className="text-xs vscode-text-tertiary mb-2">No applications yet — chart fills as pipeline grows.</p>
                  <Link to={`/applications/new?jobId=${job.id}`} className="text-xs vscode-text-link">
                    Add first application →
                  </Link>
              </div>
              ) : (
                <ResponsiveContainer width="100%" height={150}>
                  <LineChart data={heartbeatData} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={rechartsGridStroke} />
                    <XAxis dataKey="week" tick={{ fontSize: 9 }} stroke="var(--vscode-text-tertiary)" />
                    <YAxis tick={{ fontSize: 9 }} stroke="var(--vscode-text-tertiary)" />
                    <Tooltip contentStyle={rechartsTooltipStyle} labelStyle={{ color: 'var(--vscode-text-tertiary)' }} />
                    <Line type="monotone" dataKey="count" stroke="#8B80F9" strokeWidth={2} dot={{ fill: '#8B80F9' }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CandidateCollapsiblePanel>

            {/* Status & actions */}
            <CandidateCollapsiblePanel storageKey={`${job.id}-status`} title="Status & actions">
              <div className="space-y-3 py-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs vscode-text-tertiary">Ops status</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: statusColors.bg, color: statusColors.text, border: `1px solid ${statusColors.border}` }}>
                    {job.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t vscode-border pt-2">
                  <span className="text-xs vscode-text-tertiary">Publish</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold"
                    style={{ background: publishColors.bg, color: publishColors.text }}>
                    {(job.publish_status ?? 'draft').replace(/_/g, ' ')}
                    </span>
                </div>
                <div className="flex flex-col gap-2 pt-1 border-t vscode-border">
                  <button type="button"
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors border vscode-border bg-[var(--vscode-bg-tertiary)]"
                    onClick={() => { setLifecycleInitialAxis('ops'); setLifecycleModalOpen(true); }}>
                    Manage ops lifecycle →
                  </button>
                  <button type="button"
                    className="w-full btn-primary text-xs py-2 inline-flex items-center justify-center gap-1.5"
                    onClick={() => { setLifecycleInitialAxis('publish'); setLifecycleModalOpen(true); }}>
                    <Send className="w-3.5 h-3.5" /> Publish / distribute →
                  </button>
              </div>
              </div>
            </CandidateCollapsiblePanel>

            {/* Recent applications */}
            {applications.length > 0 && (
              <CandidateCollapsiblePanel storageKey={`${job.id}-recent-apps`} title="Recent applications">
                <ul className="space-y-0.5 pt-1">
                  {applications.slice(0, 5).map((app) => (
                    <li key={app.id}
                      className="flex items-center justify-between gap-2 py-1.5 border-b vscode-border last:border-0">
                      <Link to={`/applications/${app.id}`} className="flex items-center gap-2 min-w-0 group">
                        <User className="w-3.5 h-3.5 vscode-text-tertiary flex-shrink-0" />
                        <span className="text-xs vscode-text-primary group-hover:text-[var(--vscode-text-link)] font-medium truncate">
                          {app.candidate?.full_name ?? '—'}
                        </span>
                      </Link>
                      <StatusBadge status={app.status} />
                    </li>
                  ))}
                </ul>
                {applications.length > 5 && (
                  <button type="button"
                    className="text-xs vscode-text-link mt-2 block"
                    onClick={() => setTab('applications')}>
                    View all {applications.length} →
                  </button>
                )}
              </CandidateCollapsiblePanel>
            )}
          </aside>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────
          APPLICATIONS TAB
      ───────────────────────────────────────────────────────── */}
      {activeTab === 'applications' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(300px,28%)] gap-3 lg:gap-5 items-start">

          {/* ── Left: pipeline stats + submitted applications (click → modal) ── */}
          <div className="ggj-param-group overflow-hidden order-1">
            <div className="ggj-profile-toolbar">
              <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                <span className="ggj-toolbar-btn cursor-default">
                  Applications ({applications.length})
                        </span>
                {applicationsTabHeartbeat && <ActivityHeartbeatDot active variant="emerald" />}
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <Link to={`/applications?jobId=${job.id}`} className="ggj-toolbar-btn">
                  [↗ Open in hub]
                        </Link>
                <Link to={`/applications/new?jobId=${job.id}`} className="ggj-toolbar-btn">
                  [+ New application]
                          </Link>
                        </div>
        </div>

            <JobApplicationsPanel applications={applications} teamMembers={[]} />
          </div>

          {/* ── Right: heartbeat + one action card per application ── */}
          <aside className="ggj-rail-aside space-y-2 order-2 lg:sticky lg:top-4 self-start">
            {/* Hiring heartbeat */}
            <CandidateCollapsiblePanel storageKey={`${job.id}-apps-heartbeat`} title="Hiring heartbeat">
              {heartbeatData.every((d) => d.count === 0) ? (
                <p className="text-xs vscode-text-tertiary text-center py-4">No activity in last 8 weeks</p>
              ) : (
                <ResponsiveContainer width="100%" height={150}>
                  <LineChart data={heartbeatData} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={rechartsGridStroke} />
                    <XAxis dataKey="week" tick={{ fontSize: 9 }} stroke="var(--vscode-text-tertiary)" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 9 }} stroke="var(--vscode-text-tertiary)" />
                    <Tooltip contentStyle={rechartsTooltipStyle} labelStyle={{ color: 'var(--vscode-text-tertiary)' }} />
                    <Line type="monotone" dataKey="count" stroke="#8B80F9" strokeWidth={2} dot={{ fill: '#8B80F9' }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CandidateCollapsiblePanel>

            {/* One action card per application — title = candidate name */}
            {applications.length === 0 ? (
              <div className="ggj-param-group p-3 text-xs vscode-text-tertiary">No applications yet.</div>
            ) : (
              sortedAppsForJobRail.map((app) => {
                const staleDays = Math.floor(
                  (Date.now() - new Date(app.status_updated_at).getTime()) / 86400000
                );
                const isTerminal = ['placed', 'rejected', 'closed', 'withdrawn'].includes(app.status);
                const isStale = !isTerminal && staleDays >= 5;
                const lockExpiring =
                  app.pipeline_lock_expires_at &&
                  new Date(app.pipeline_lock_expires_at).getTime() < Date.now() + 86400000 * 2;
                  return (
                  <CandidateCollapsiblePanel
                    key={app.id}
                    storageKey={`${job.id}-app-rail-${app.id}`}
                    title={(app.candidate?.full_name ?? 'Candidate').toUpperCase()}
                  >
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="vscode-text-secondary">Stage</span>
                        <span className="vscode-text-primary font-medium">
                          {PIPELINE_STATUS_LABEL[app.status as ApplicationStatus] ?? app.status}
                          </span>
                        </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="vscode-text-secondary">In stage</span>
                        <span className={`font-medium ${isStale ? 'text-amber-500' : 'vscode-text-primary'}`}>
                          {staleDays < 1 ? 'today' : `${staleDays}d`}
                        </span>
                      </div>
                      {app.candidate?.current_title && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="vscode-text-secondary">Role</span>
                          <span className="vscode-text-primary truncate max-w-[160px] text-right">
                            {app.candidate.current_title}
                          </span>
                        </div>
                      )}
                      {isStale && (
                        <p className="text-amber-600 dark:text-amber-400 font-medium border border-amber-400/30 bg-amber-50/60 dark:bg-amber-950/30 rounded px-2 py-1 leading-snug">
                          ⚠ Stale — no movement in {staleDays}d
                        </p>
                      )}
                      {lockExpiring && (
                        <p className="text-red-500 font-medium border border-red-400/30 bg-red-50/60 dark:bg-red-950/30 rounded px-2 py-1 leading-snug">
                          🔒 Lock expiring soon
                        </p>
                      )}
                      {isTerminal && (
                        <p className="vscode-text-tertiary italic">No action needed — terminal stage.</p>
                      )}
                      {!isStale && !lockExpiring && !isTerminal && (
                        <p className="vscode-text-tertiary">No immediate action needed.</p>
                      )}
                      <Link to={`/applications/${app.id}`} className="ggj-toolbar-btn block mt-1 text-center">
                        [↗ Open application]
                          </Link>
                      {app.candidate_id && (
                        <Link to={`/candidates/${app.candidate_id}`} className="ggj-toolbar-btn block text-center">
                          [→ View profile]
                            </Link>
                          )}
                        </div>
                  </CandidateCollapsiblePanel>
                );
              })
            )}
          </aside>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────
          SMART MATCHES TAB
      ───────────────────────────────────────────────────────── */}
      {activeTab === 'matches' && id && (
        <JobMatchesTab jobId={id} />
      )}

      {/* ─────────────────────────────────────────────────────────
          CLIENT SYNC (was PLUGIN) TAB
      ───────────────────────────────────────────────────────── */}
      {activeTab === 'integration' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(300px,28%)] gap-3 lg:gap-5 items-start">

          <div className="ggj-param-group overflow-hidden order-1">
            {/* Toolbar with Edit / Save / Discard */}
            <div className="ggj-profile-toolbar">
              <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                <span className="ggj-toolbar-btn cursor-default flex items-center gap-2">
                  <Plug className="w-3.5 h-3.5" />
                  Client sync — connect &amp; fetch jobs
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                {isIntegrationEditing ? (
                  <>
                    <button
                      type="button"
                      className={`${detailTbBtn} ${detailTbNeutral}`}
                      onClick={() => setIsIntegrationEditing(false)}
                    >
                      <Save className="gg-toolbar-ic gg-toolbar-ic--violet shrink-0" aria-hidden />
                      <span>[Save]</span>
                    </button>
                    <button
                      type="button"
                      className={`${detailTbBtn} ${detailTbDanger}`}
                      onClick={() => setIsIntegrationEditing(false)}
                    >
                      <RotateCcw className="gg-toolbar-ic gg-toolbar-ic--red shrink-0" aria-hidden />
                      <span>[Discard]</span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={`${detailTbBtn} ${detailTbEdit}`}
                    onClick={() => setIsIntegrationEditing(true)}
                  >
                    <Pencil className="h-4 w-4 shrink-0 text-[var(--vscode-text-link)]" aria-hidden />
                    <span>Edit</span>
                  </button>
                )}
              </div>
            </div>

            <div className="p-3 space-y-3">
            {/* ── Connection settings ── */}
            <div className="ggj-param-section-block">
            <div className="ggj-profile-section-h">CONNECTION SETTINGS</div>
            <div className="ggj-profile-section-body">
              <p className="text-xs vscode-text-secondary leading-relaxed mb-4">
                Connect to the client&apos;s external ATS, HRIS, or job portal to pull live job postings into this record.
              </p>
              <dl className="ggj-attr-grid">
                <dt className={PARAM_DT}>Connection type</dt>
                <dd className={PARAM_DD}>
                  <select className="input w-full max-w-sm text-sm" disabled={!isIntegrationEditing}>
                <option>Select integration…</option>
                <option>REST API</option>
                    <option>OAuth (LinkedIn, Naukri, etc.)</option>
                <option>Webhook (push from client)</option>
              </select>
                </dd>
                <dt className={PARAM_DT}>API endpoint URL</dt>
                <dd className={PARAM_DD}>
                  <input className="input w-full max-w-sm text-sm"
                    placeholder="https://client-ats.example.com/jobs"
                    disabled={!isIntegrationEditing} />
                </dd>
                <dt className={PARAM_DT}>API key / token</dt>
                <dd className={PARAM_DD}>
                  <input className="input w-full max-w-sm text-sm" type="password"
                    placeholder="••••••••"
                    disabled={!isIntegrationEditing} />
                </dd>
                <dt className={PARAM_DT}>Connection status</dt>
                <dd className={PARAM_DD}>
                  <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    Not connected
                  </span>
                </dd>
              </dl>
              {isIntegrationEditing && (
                <div className="flex flex-wrap gap-2 mt-4 pt-2 border-t border-[var(--vscode-border-divider)]">
                  <button type="button" className="btn-secondary text-xs inline-flex items-center gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5" /> Test connection
                  </button>
                  <button type="button" className="btn-primary text-xs">Sync jobs now</button>
            </div>
              )}
            </div>
            </div>

            {/* ── Fetch configuration ── */}
            <div className="ggj-param-section-block">
            <div className="ggj-profile-section-h">FETCH CONFIGURATION</div>
            <div className="ggj-profile-section-body">
              <dl className="ggj-attr-grid">
                <dt className={PARAM_DT}>Sync frequency</dt>
                <dd className={PARAM_DD}>
                  <select className="input w-full max-w-sm text-sm" disabled={!isIntegrationEditing}>
                    <option>Manual only</option>
                    <option>Every hour</option>
                    <option>Every 6 hours</option>
                    <option>Daily</option>
                  </select>
                </dd>
                <dt className={PARAM_DT}>Auto-map fields</dt>
                <dd className={PARAM_DD}>
                  <span className="vscode-text-tertiary text-xs">Not configured</span>
                </dd>
                <dt className={PARAM_DT}>Records synced</dt>
                <dd className={PARAM_DD}>—</dd>
                <dt className={PARAM_DT}>Last synced</dt>
                <dd className={PARAM_DD}>—</dd>
                <dt className={PARAM_DT}>Next sync</dt>
                <dd className={PARAM_DD}>—</dd>
              </dl>
            </div>
            </div>

            <div className="ggj-param-section-block">
            <div className="ggj-profile-section-h">COMING SOON</div>
            <div className="ggj-profile-section-body">
              <p className="text-xs vscode-text-secondary leading-relaxed">
                Configure per-client connections under <Link to={clientHref} className="vscode-text-link">Data → {job.client?.name ?? 'Clients'}</Link>, or contact support for enterprise integrations.
              </p>
            </div>
            </div>
            </div>
        </div>

          <aside className="ggj-rail-aside space-y-2 order-2 lg:sticky lg:top-4 self-start">
            <CandidateCollapsiblePanel storageKey={`${job.id}-integration-client`} title="Client">
              <div className="space-y-0 text-xs py-1">
                {[
                  { label: 'Client',      value: <Link to={clientHref} className="vscode-text-link font-medium">{job.client?.name ?? '—'}</Link> },
                  { label: 'SPOC email',  value: job.client?.contact_email ?? '—' },
                  { label: 'Job status',  value: job.status.replace(/_/g,' ') },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between border-b vscode-border py-1.5 last:border-0">
                    <span className="vscode-text-tertiary">{label}</span>
                    <span className="vscode-text-secondary font-medium text-right truncate max-w-[160px]">{value}</span>
              </div>
                ))}
              </div>
            </CandidateCollapsiblePanel>
            <CandidateCollapsiblePanel storageKey={`${job.id}-integration-sync`} title="Sync status">
              <div className="space-y-0 text-xs py-1">
                <div className="flex items-center gap-2 px-2 py-1.5 rounded border vscode-border mb-2"
                  style={{ background: 'var(--vscode-bg-tertiary)' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                  <span className="vscode-text-tertiary">Not connected — preview only</span>
            </div>
                <a href="mailto:support@ggj.com"
                  className="text-xs vscode-text-link inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Contact support to enable
                </a>
              </div>
            </CandidateCollapsiblePanel>
          </aside>
              </div>
      )}

      {/* ─────────────────────────────────────────────────────────
          PARAMETERS TAB
      ───────────────────────────────────────────────────────── */}
      {activeTab === 'parameters' && (
        <div className="ggj-param-group overflow-hidden">
          <div className="ggj-profile-toolbar">
            <span className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">Parameters</span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={`${detailTbBtn} ${detailTbEdit}`}
                onClick={() => {
                  setActiveTab('overview');
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('tab');
                    next.set('edit', '1');
                    return next;
                  }, { replace: true });
                }}
              >
                <Pencil className="h-4 w-4 shrink-0 text-[var(--vscode-text-link)]" aria-hidden />
                <span>Edit</span>
              </button>
            </div>
          </div>
          <div className="p-3 space-y-3">
          {/* ── Object — name, description, type (matches candidate Parameters) ── */}
          <div className="ggj-param-section-block border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)]">
            <div className="ggj-profile-section-h">Object</div>
            <div className="ggj-profile-section-body">
              <dl className="ggj-attr-grid">
                <dt className={PARAM_DT}>Name</dt>
                <dd className={`${PARAM_DD} font-medium`}>{job.title}</dd>
                <dt className={PARAM_DT}>Object type</dt>
                <dd className={`${PARAM_DD} vscode-text-secondary`}>Job (read-only)</dd>
                <dt className={PARAM_DT}>Description</dt>
                <dd className={`${PARAM_DD} min-w-0 lg:col-span-3`}>
                  <textarea
                    readOnly
                    tabIndex={-1}
                    rows={5}
                    value={job.description?.trim() ? job.description : ''}
                    placeholder="—"
                    aria-label="Description"
                    className="ggj-param-readonly-textarea"
                  />
                </dd>
              </dl>
            </div>
          </div>

          {/* ── Role & client ── */}
          <div className="ggj-param-section-block">
          <div className="ggj-profile-section-h">Role &amp; client</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Job role</dt>
              <dd className={`${PARAM_DD} font-medium`}>{job.title}</dd>
              <dt className={PARAM_DT}>Role category</dt>
              <dd className={PARAM_DD}>
                {jdTaxonomy.category ? (
                  <span className="inline-flex rounded-full border border-[color:var(--vscode-primary)]/30 bg-[color-mix(in_srgb,var(--vscode-primary)_8%,transparent)] px-2 py-0.5 text-[11px] font-semibold">
                    {jdTaxonomy.category}
                  </span>
                ) : (
                  <span className="vscode-text-tertiary text-sm">— (from JD extraction)</span>
                )}
              </dd>
              <dt className={PARAM_DT}>Seniority</dt>
              <dd className={PARAM_DD}>
                {jdTaxonomy.seniority ? (
                  <span className="inline-flex rounded-full border border-[color:var(--vscode-border-primary)] px-2 py-0.5 text-[11px] font-medium vscode-text-secondary">
                    {jdTaxonomy.seniority}
                  </span>
                ) : (
                  <span className="vscode-text-tertiary text-sm">—</span>
                )}
              </dd>
              <dt className={PARAM_DT}>Hiring company industry</dt>
              <dd className={PARAM_DD}>{jdTaxonomy.industry ?? <span className="vscode-text-tertiary text-sm">—</span>}</dd>
              <dt className={PARAM_DT}>Client</dt>
              <dd className={PARAM_DD}>
                <Link to={clientHref} className="vscode-text-link">
                  {job.client?.name ?? '—'}
                </Link>
              </dd>
              <dt className={PARAM_DT}>Location</dt>
              <dd className={PARAM_DD}>{job.location ?? '—'}</dd>
              <dt className={PARAM_DT}>Experience</dt>
              <dd className={PARAM_DD}>
                  {job.experience_min != null || job.experience_max != null
                    ? `${job.experience_min ?? 0}–${job.experience_max ?? '∞'} yrs`
                    : '—'}
              </dd>
              <dt className={PARAM_DT}>JD URL</dt>
              <dd className={PARAM_DD}>
                {job.jd_url ? (
                  <a href={job.jd_url} target="_blank" rel="noopener noreferrer"
                    className="vscode-text-link inline-flex items-center gap-1 text-xs break-all">
                    <ExternalLink className="w-3 h-3 shrink-0" /> {job.jd_url}
                  </a>
                ) : '—'}
              </dd>
              <dt className={PARAM_DT}>Organisation</dt>
              <dd className={PARAM_DD}>{orgName ?? '—'}</dd>
            </dl>
              </div>
          </div>

          {/* ── Status & lifecycle ── */}
          <div className="ggj-param-section-block">
          <div className="ggj-profile-section-h">Status &amp; lifecycle</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Ops status</dt>
              <dd className={PARAM_DD}>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                  style={{ background: statusColors.bg, color: statusColors.text, border: `1px solid ${statusColors.border}` }}>
                  {job.status.replace(/_/g, ' ')}
                </span>
              </dd>
              <dt className={PARAM_DT}>Publish status</dt>
              <dd className={PARAM_DD}>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                  style={{ background: publishColors.bg, color: publishColors.text }}>
                  {(job.publish_status ?? 'draft').replace(/_/g, ' ')}
                </span>
              </dd>
              <dt className={PARAM_DT}>Opened</dt>
              <dd className={PARAM_DD}>{fmtDate(job.opened_at)}</dd>
              <dt className={PARAM_DT}>Filled</dt>
              <dd className={PARAM_DD}>{fmtDate(job.filled_at)}</dd>
              <dt className={PARAM_DT}>Published at</dt>
              <dd className={PARAM_DD}>{fmtDate(job.published_at)}</dd>
              <dt className={PARAM_DT}>Scheduled publish</dt>
              <dd className={PARAM_DD}>{fmtDateTime(job.scheduled_publish_at)}</dd>
              <dt className={PARAM_DT}>Auto-unpublish</dt>
              <dd className={PARAM_DD}>{fmtDateTime(job.scheduled_unpublish_at)}</dd>
            </dl>
              </div>
          </div>

          {/* ── Publishing channels ── */}
          <div className="ggj-param-section-block">
          <div className="ggj-profile-section-h">Publishing &amp; distribution</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Channels</dt>
              <dd className={`${PARAM_DD} lg:col-span-3`}>
                {normalizeJobPublishChannels(job.publish_channels).length === 0 ? (
                  <span className="vscode-text-tertiary">None — set via publish flow</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {normalizeJobPublishChannels(job.publish_channels).map((cid) => {
                      const m = metaForChannel(cid);
                      return (
                        <span key={cid} className="text-[11px] px-2 py-0.5 rounded vscode-text-secondary"
                          style={{ background: 'var(--vscode-bg-tertiary)', border: '1px solid var(--vscode-border-primary)' }}>
                          {m?.label ?? cid}
                        </span>
                      );
                    })}
                  </div>
                )}
              </dd>
              <dt className={PARAM_DT}>Careers URL</dt>
              <dd className={`${PARAM_DD} lg:col-span-3`}>
                {job.publish_status === 'published' && job.status === 'open' && orgSlug ? (
                  <a href={`${window.location.origin}/careers/${encodeURIComponent(orgSlug)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="vscode-text-link inline-flex items-center gap-1 text-xs break-all">
                    <ExternalLink className="w-3 h-3" /> /careers/{orgSlug}
                  </a>
                ) : '—'}
              </dd>
            </dl>
              </div>
          </div>

          {/* ── Required skills ── */}
          <div className="ggj-param-section-block">
          <div className="ggj-profile-section-h">Required skills</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Skills</dt>
              <dd className={`${PARAM_DD} lg:col-span-3`}>
            {(job.skills_required ?? []).length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                    {(job.skills_required ?? []).map((s) => (
                      <span key={s} className="px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{ background: 'rgba(139,128,249,0.1)', color: '#8B80F9', border: '1px solid rgba(139,128,249,0.3)' }}>
                        {s}
                      </span>
                ))}
              </div>
            ) : (
                  <span className="vscode-text-tertiary">No skills specified.</span>
                )}
              </dd>
            </dl>
          </div>
          </div>

          {/* ── Careers SEO (JD body lives under Object → Description) ── */}
          <div className="ggj-param-section-block">
          <div className="ggj-profile-section-h">Careers SEO</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              {job.career_seo_summary ? (
                <>
                  <dt className={PARAM_DT}>SEO summary</dt>
                  <dd className={`${PARAM_DD} lg:col-span-3`}>
                    <textarea
                      readOnly
                      value={job.career_seo_summary}
                      rows={3}
                      className="input w-full text-sm vscode-text-primary leading-relaxed resize-y min-h-[60px]"
                    />
                  </dd>
                </>
              ) : (
                <p className="text-xs vscode-text-tertiary col-span-full">No careers SEO summary set.</p>
              )}
            </dl>
          </div>
          </div>

          {/* ── System attributes ── */}
          <div className="ggj-param-section-block">
          <div className="ggj-profile-section-h">System attributes</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Job ID</dt>
              <dd className={PARAM_DD}><span className="font-mono text-xs break-all">{job.id}</span></dd>
              <dt className={PARAM_DT}>Org ID</dt>
              <dd className={PARAM_DD}><span className="font-mono text-xs break-all">{job.org_id}</span></dd>
              <dt className={PARAM_DT}>Client ID</dt>
              <dd className={PARAM_DD}><span className="font-mono text-xs break-all">{job.client_id}</span></dd>
              <dt className={PARAM_DT}>Created by</dt>
              <dd className={PARAM_DD}><span className="font-mono text-xs break-all">{job.created_by ?? '—'}</span></dd>
              <dt className={PARAM_DT}>Created when</dt>
              <dd className={PARAM_DD}>{fmtDateTime(job.created_at)}</dd>
              <dt className={PARAM_DT}>Modified when</dt>
              <dd className={PARAM_DD}>—</dd>
              <dt className={PARAM_DT}>Modified by</dt>
              <dd className={PARAM_DD}>—</dd>
            </dl>
          </div>
          </div>
          </div>
        </div>
      )}

          </>
        }
      />

    </div>
  );
}
